import React from 'react';
import {Provider} from 'mobx-react';
import {Resources} from "@morningtrain/resources";
import {Repository} from "@morningtrain/helpers";
import OperationsContext from "./contexts/OperationsContext";

export default class OperationRepository extends React.Component {

    static globalRepository;

    constructor(props) {
        super(props);

        if(!OperationRepository.globalRepository) {
            OperationRepository.globalRepository = new Repository()
        }

        if(props.global === true) {
            this.repository = OperationRepository.globalRepository
        } else {
            this.repository = new Repository();
        }

        this.keys = [];

    }

    static get defaultProps() {
        return {
            storageDriver: null
        };
    }

    /////////////////////////////////
    // Context helpers
    /////////////////////////////////

    stringifyContext(operationContext) {

        if (typeof (operationContext) === 'string') {
            return operationContext;
        }

        if (!operationContext || typeof (operationContext) !== 'object') {
            return;
        }

        let keys = Object.keys(operationContext);

        if (Array.isArray(keys) && keys.length > 0) {

            /// We need to uniform context keys for uniqueness by sorting them
            keys = keys.sort();

            return keys
                .map(key => {
                    return typeof (operationContext[key]) === 'undefined' ? null : key;
                })
                .filter(e => e)
                .map(key => {
                    return `${key}=${operationContext[key]}`;
                }).join('&');
        }

        return '';
    }

    /////////////////////////////////
    // Cache helpers
    /////////////////////////////////

    cache(key, callback) {

        /// If no callback method was provided - some DEV probably misconfigured something
        if (typeof (callback) !== 'function') {
            throw new Error('Tried to get operation from cache, but no callback was supplied for: ' + key);
        }

        if (!this.repository.has(key)) {
            this.keys[this.keys.length] = key;
            this.repository.set(key, callback());
        }

        return this.repository.get(key);
    }

    /////////////////////////////////
    // Helpers
    /////////////////////////////////

    getBySlug(slug, operationContext = null) {

        let fragments = slug.split('.');

        if (fragments.length < 3) {
            throw new Error('Tried to get operation by slug, but it might be malformed: ' + slug);
        }

        let namespace = fragments.shift();
        let operationName = fragments.pop();
        let resourceName = fragments.join('.');

        return this.get(namespace, resourceName, operationName, operationContext);
    }

    buildSlug(namespace, resourceName, operationName, operationContext = null) {
        return [
            namespace,
            resourceName,
            operationName,
            this.stringifyContext(operationContext)
        ].join('.');
    }

    get(namespace, resourceName, operationName, operationContext = null, options = {}) {


        /// Generate operation slug
        const slug = this.buildSlug(namespace, resourceName, operationName, operationContext);

        /// Get operation from cache - or create if missing
        let operation = this.cache(slug, () => {
            let operation = this.createOperation(namespace, resourceName, operationName, options)

            if(this.hasStorageDriver() && options.persist === true) {
                operation.onData(data => {
                    this.storageDriver.setItem('operation_data_' + slug, JSON.stringify(data));
                });
            }


            return operation;
        });

        if(this.hasStorageDriver() && options.persist === true) {

            this.storageDriver.getItem('operation_data_' + slug)
                .then(data => {
                    if (data) {
                        operation.setData(data);
                    }
                })
                .catch(err => {
                    console.error(err);
                });

        }

        return operation;
    }

    getFromRoute(route, operationContext = null, options = {}) {
        const args = route.split('.')

        const namespace      = args.shift();
        const operationName = args.pop();
        const resourceName  = args.join('.');

        return this.get(namespace, resourceName, operationName, operationContext, options);
    }

    createOperation(namespace, resourceName, operationName, options = {}) {
        const operation = Resources.make(namespace, resourceName).operation(operationName, options);

        if (this.onCreateCallbacks.length > 0) {
            this.onCreateCallbacks.forEach(callback => {
                if (typeof (callback) === 'function') {
                    callback(operation);
                }
            })
        }

        return operation;
    }

    onCreateCallbacks = [];

    onCreate(callback) {

        if(this.keys.length > 0) {
            this.keys.map(key => {
                if (typeof (callback) === 'function') {
                    callback(this.repository.get(key));
                }
            });
        }

        this.onCreateCallbacks.push(callback);
    }

    /////////////////////////////////
    // Storage driver helpers
    /////////////////////////////////

    hasStorageDriver() {
        return this.storageDriver !== null;
    }

    get storageDriver() {
        return this.props.storageDriver;
    }

    /////////////////////////////////
    // Rendering
    /////////////////////////////////

    render() {
        return (
            <OperationsContext.Provider value={this} >
                <Provider operations={this}>
                    <React.Fragment>
                        {this.props.children}
                    </React.Fragment>
                </Provider>
            </OperationsContext.Provider>
        );
    }
}
